import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Container,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Check,
  Info,
  LockOutlined,
  Description,
  Shield,
  Warning
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { validateConsentToken, giveConsentForCreditCheck } from '../../api/consent';

interface ApplicationData {
  id: string;
  tenantFirstName: string;
  tenantLastName: string;
  tenantEmail: string;
  apartmentNumber: string;
  address: string;
  city: string;
  zipCode?: string;
}

type ErrorCode = 'TOKEN_NOT_FOUND' | 'CONSENT_ALREADY_GIVEN' | 'LINK_EXPIRED' | 'INTERNAL_ERROR' | null;

const ConsentPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [applicationData, setApplicationData] = useState<ApplicationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<ErrorCode>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorCode(null);

      if (!token) {
        setError('Token saknas');
        setErrorCode('TOKEN_NOT_FOUND');
        setLoading(false);
        return;
      }

      // Validera token via Firebase Function
      const result = await validateConsentToken(token);

      if (!result.success) {
        setError(result.error || 'Ogiltig eller utgången länk');
        setErrorCode(result.errorCode || 'INTERNAL_ERROR');
        setLoading(false);
        return;
      }

      if (result.data) {
        setApplicationData(result.data);
      } else {
        setError('Kunde inte hämta ansökningsdata');
        setErrorCode('INTERNAL_ERROR');
      }

      setLoading(false);
    } catch (error: any) {
      console.error('Error validating consent token:', error);
      setError('Ett fel uppstod vid validering av länken');
      setErrorCode('INTERNAL_ERROR');
      setLoading(false);
    }
  };

  const handleSubmitConsent = async () => {
    if (!token || !consentGiven || !gdprConsent) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Ge samtycke via Firebase Function
      const result = await giveConsentForCreditCheck(token);

      if (!result.success) {
        setError(result.error || 'Ett fel uppstod när samtycket skulle sparas');
        setSubmitting(false);
        return;
      }

      setSuccess(true);

      // Redirect efter 3 sekunder
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      console.error('Error submitting consent:', error);
      setError('Ett fel uppstod när samtycket skulle sparas. Försök igen.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !applicationData) {
    // Visa specifikt UI för "samtycke redan givet"
    if (errorCode === 'CONSENT_ALREADY_GIVEN') {
      return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3
              }}>
                <Check sx={{ fontSize: 48, color: 'success.dark' }} />
              </Box>

              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Samtycke redan skickat
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Du har redan gett ditt samtycke för kreditupplysning för denna ansökan.
                Du behöver inte göra något mer.
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Om du har frågor, vänligen kontakta support@hyria.se
              </Typography>

              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  background: '#62B8E9',
                  '&:hover': {
                    background: '#4FA8D5',
                  }
                }}
              >
                Gå till startsidan
              </Button>
            </CardContent>
          </Card>
        </Container>
      );
    }

    // Visa specifikt UI för "länk finns inte"
    if (errorCode === 'TOKEN_NOT_FOUND' || errorCode === 'LINK_EXPIRED') {
      return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3
              }}>
                <Warning sx={{ fontSize: 48, color: 'warning.dark' }} />
              </Box>

              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Länken är ogiltig
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {errorCode === 'LINK_EXPIRED'
                  ? 'Denna länk har gått ut eller är inte längre giltig.'
                  : 'Vi kunde inte hitta någon samtyckesbegäran kopplad till denna länk.'}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Om du tror att detta är ett fel, vänligen kontakta support@hyria.se
              </Typography>

              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  background: '#62B8E9',
                  '&:hover': {
                    background: '#4FA8D5',
                  }
                }}
              >
                Gå till startsidan
              </Button>
            </CardContent>
          </Card>
        </Container>
      );
    }

    // Generiskt fel
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3
            }}>
              <Warning sx={{ fontSize: 48, color: 'error.dark' }} />
            </Box>

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Något gick fel
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Försök igen eller kontakta support@hyria.se om problemet kvarstår.
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                background: '#62B8E9',
                '&:hover': {
                  background: '#4FA8D5',
                }
              }}
            >
              Gå till startsidan
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3
            }}>
              <Check sx={{ fontSize: 48, color: 'success.dark' }} />
            </Box>

            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Tack för ditt samtycke!
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Vi har mottagit ditt samtycke för kreditupplysning.
              Du kommer att få ett mail när kreditkontrollen är genomförd.
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Du omdirigeras till startsidan om några sekunder...
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: '#62B8E9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          mb: 2
        }}>
          <LockOutlined sx={{ fontSize: 32, color: 'white' }} />
        </Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Samtycke för kreditupplysning
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          För lägenhet {applicationData?.apartmentNumber} på {applicationData?.address}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Applicant Info */}
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Info sx={{ mr: 1, color: 'primary.main' }} />
              Din information
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Namn:</strong> {applicationData?.tenantFirstName} {applicationData?.tenantLastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>E-post:</strong> {applicationData?.tenantEmail}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Lägenhet:</strong> {applicationData?.apartmentNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Adress:</strong> {applicationData?.address}, {applicationData?.city}
              </Typography>
            </Box>
          </Paper>

          {/* Information about credit check */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Shield sx={{ mr: 1, color: 'primary.main' }} />
            Vad innebär en kreditupplysning?
          </Typography>

          <Typography variant="body2" paragraph sx={{ mb: 3 }}>
            En kreditupplysning är en kontroll av din ekonomiska historik som hjälper hyresvärden
            att bedöma din betalningsförmåga. Detta är ett standardförfarande vid uthyrning av bostäder.
          </Typography>

          <List sx={{ mb: 3 }}>
            <ListItem>
              <ListItemIcon>
                <Check color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Inkomstverifiering"
                secondary="Kontroll av beskattad inkomst relevant till hyran."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Check color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Betalningshistorik"
                secondary="Granskning av eventuella betalningsanmärkningar."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Check color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Skulder hos Kronofogden"
                secondary="Kontroll av eventuella obetalda skulder."
              />
            </ListItem>
          </List>

          {/* GDPR Information */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>GDPR och din integritet:</strong> Dina personuppgifter behandlas enligt GDPR.
              Kreditupplysningen utförs av Hyria i samarbete med godkänd kreditupplysningsföretag.
              Informationen används endast för bedömning av denna hyresansökan och raderas enligt
              gällande dataskyddsregler.
            </Typography>
          </Alert>

          {/* Consent checkboxes */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2">
                  Jag samtycker till att en kreditupplysning genomförs för min hyresansökan.
                </Typography>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={gdprConsent}
                  onChange={(e) => setGdprConsent(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2">
                  Jag har läst och förstått hur mina personuppgifter kommer att behandlas enligt{' '}
                  <a
                    href="/HyriaPersonuppgiftspolicy.docx.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#62B8E9', textDecoration: 'underline' }}
                  >
                    personuppgiftspolicy
                  </a>
                  .
                </Typography>
              }
            />
          </Box>

          {/* Warning */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Observera:</strong> Om du inte ger ditt samtycke kan din hyresansökan inte behandlas vidare.
            </Typography>
          </Alert>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
              disabled={submitting}
              size="large"
            >
              Avbryt
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitConsent}
              disabled={!consentGiven || !gdprConsent || submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Check />}
              size="large"
              sx={{
                background: '#62B8E9',
                '&:hover': {
                  background: '#4FA8D5',
                }
              }}
            >
              {submitting ? 'Skickar...' : 'Ge mitt samtycke'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Footer info */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Har du frågor? Kontakta support@hyria.se
        </Typography>
      </Box>
    </Container>
  );
};

export default ConsentPage;